import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import DashboardLayout from "../../components/DashboardLayout/DashboardLayout";
import AddProductServiceModal, { type ProductServiceFormData } from "../../components/Modal/AddProductServiceModal";
import ConfirmDeleteModal from "../../components/Modal/ConfirmDeleteModal";
import HSCodeSelectionModal, { type HSCodeSuggestion } from "../../components/Modal/HSCodeSelectionModal";
import { DashboardCardSkeleton } from "../../components/Skeleton";
import { getApiErrorMessage } from "../../lib/apiError";
import type { FieldWarning, Product } from "../../services/products/generated/model";
import {
  useProductsCreateCreate,
  useProductsDeleteDestroy,
  useProductsMyProductsList,
  useProductsUpdateUpdate,
} from "../../services/products/generated/products/products";
import type { Service } from "../../services/services/generated/model";
import {
  useServicesCreateCreate,
  useServicesDeleteDestroy,
  useServicesMyServicesList,
  useServicesUpdateUpdate,
} from "../../services/services/generated/services/services";
import "./ProductServicePage.css";

interface ProductServiceData {
  id: string;
  name: string;
  description: string;
  type: "product" | "service";
  hsCode?: string;
  // OQ-05: the HS code isn't in the official HS table (saved, but flagged).
  hsCodeUnverified?: boolean;
  hsDescription: string;
  category: string;
  quantity?: string;
  quantityUnit?: string;
  price?: string;
  currency?: string;
  deliveryTime?: string;
  region?: string;
  images?: string[];
}

function mapProductToItem(p: Product): ProductServiceData {
  return {
    id: String(p.id),
    name: p.name,
    description: p.description || "",
    type: "product",
    hsCode: p.hsCode,
    hsCodeUnverified: p.hsCodeUnverified,
    hsDescription: p.media?.[0]?.description || "",
    category: p.category || "",
    quantity: String(p.quantity || 0),
    quantityUnit: p.quantityUnit || "",
    price: p.price,
    currency: p.currency,
    deliveryTime: p.deliveryTime,
    region: p.region || "",
    images: p.media?.map((m) => m.fileUrl) ?? [],
  };
}

function mapServiceToItem(s: Service): ProductServiceData {
  return {
    id: String(s.id),
    name: s.name,
    description: s.description || "",
    type: "service",
    hsDescription: s.media?.[0]?.description || "",
    category: s.category || "",
    price: s.price,
    currency: s.currency,
    deliveryTime: s.deliveryTime,
    region: s.region || "",
    images: s.media?.map((m) => m.fileUrl) ?? [],
  };
}

const ProductServicePage = () => {
  const { t } = useTranslation(["company", "common"]);
  const queryClient = useQueryClient();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isHsModalOpen, setIsHsModalOpen] = useState(false);
  const [hsModalError, setHsModalError] = useState<string | null>(null);
  const [editingItem, setEditingItem] = useState<ProductServiceData | null>(null);
  const [pendingFormData, setPendingFormData] = useState<ProductServiceFormData | null>(null);
  const [deletingItem, setDeletingItem] = useState<ProductServiceData | null>(null);
  const [editError, setEditError] = useState<string | null>(null);
  const [tableSearch, setTableSearch] = useState("");
  // Non-blocking save warnings (e.g. OQ-05 unknown HS code).
  const [saveWarnings, setSaveWarnings] = useState<readonly FieldWarning[]>([]);

  // Fetch products and services using Orval-generated hooks
  const { data: productsResponse, isLoading: productsLoading, error: productsError } = useProductsMyProductsList();

  const { data: servicesResponse, isLoading: servicesLoading, error: servicesError } = useServicesMyServicesList();

  // Mutation hooks
  const createProductMutation = useProductsCreateCreate();
  const createServiceMutation = useServicesCreateCreate();
  const updateProductMutation = useProductsUpdateUpdate();
  const updateServiceMutation = useServicesUpdateUpdate();
  const deleteProductMutation = useProductsDeleteDestroy();
  const deleteServiceMutation = useServicesDeleteDestroy();

  // Combine loading and error states
  const loading = productsLoading || servicesLoading;
  const listError = productsError || servicesError;

  // Extract data from paginated responses
  // The API returns paginated objects { count, next, previous, results: [] }
  // but Swagger spec incorrectly defines them as plain arrays
  const productsData = (productsResponse as any)?.results ?? (Array.isArray(productsResponse) ? productsResponse : []);
  const servicesData = (servicesResponse as any)?.results ?? (Array.isArray(servicesResponse) ? servicesResponse : []);

  // Combine and map products and services
  const productItems: ProductServiceData[] = productsData.map(mapProductToItem);
  const serviceItems: ProductServiceData[] = servicesData.map(mapServiceToItem);
  const products = [...productItems, ...serviceItems];

  // Client-side filter over the loaded catalog (name, HS code, category).
  const normalizedTableSearch = tableSearch.trim().toLowerCase();
  const visibleProducts = normalizedTableSearch
    ? products.filter((item) =>
        [item.name, item.hsCode, item.category].some((field) =>
          (field ?? "").toLowerCase().includes(normalizedTableSearch),
        ),
      )
    : products;

  const handleOpenModal = () => {
    setEditingItem(null);
    setPendingFormData(null);
    setHsModalError(null);
    setEditError(null);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingItem(null);
    setEditError(null);
  };

  const handleEdit = (item: ProductServiceData) => {
    setEditingItem(item);
    setPendingFormData(null);
    setEditError(null);
    setIsModalOpen(true);
  };

  const handleDeleteClick = (item: ProductServiceData) => {
    setDeletingItem(item);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deletingItem) return;

    const mutation = deletingItem.type === "product" ? deleteProductMutation : deleteServiceMutation;

    mutation.mutate(
      { id: Number(deletingItem.id) },
      {
        onSuccess: () => {
          // Invalidate queries to refresh the list
          queryClient.invalidateQueries({
            queryKey:
              deletingItem.type === "product" ? ["/api/v1/products/myProducts/"] : ["/api/v1/services/myServices/"],
          });
          setDeletingItem(null);
          setIsDeleteModalOpen(false);
        },
        onError: (err) => {
          console.error("Delete failed:", err); // i18n-ignore
          setDeletingItem(null);
          setIsDeleteModalOpen(false);
        },
      },
    );
  };

  const handleSave = async (data: ProductServiceFormData) => {
    if (editingItem?.id) {
      const media = (data.images || []).map((image, index) => ({
        fileUrl: image,
        mediaType: "image" as const,
        description: "",
        order: index,
      }));

      const baseUpdatePayload = {
        name: data.name.trim(),
        description: data.description.trim(),
        category: data.category.trim(),
        price: data.price.trim(),
        currency: data.currency,
        deliveryTime: data.deliveryTime.trim(),
        region: data.region.trim(),
        media,
      };

      if (editingItem.type === "product") {
        updateProductMutation.mutate(
          {
            id: Number(editingItem.id),
            data: {
              ...baseUpdatePayload,
              hsCode: editingItem.hsCode,
              quantity: Number(data.quantity || 0),
              quantityUnit: data.quantityUnit || "",
            },
          },
          {
            onSuccess: (saved) => {
              setSaveWarnings(saved.warnings ?? []);
              queryClient.invalidateQueries({ queryKey: ["/api/v1/products/myProducts/"] });
              setIsModalOpen(false);
              setEditingItem(null);
              setEditError(null);
            },
            onError: (err) => {
              setEditError(getApiErrorMessage(err, t("productService.errors.updateFailed")));
            },
          },
        );
        return;
      }

      updateServiceMutation.mutate(
        { id: Number(editingItem.id), data: baseUpdatePayload },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["/api/v1/services/myServices/"] });
            setIsModalOpen(false);
            setEditingItem(null);
            setEditError(null);
          },
          onError: (err) => {
            setEditError(getApiErrorMessage(err, t("productService.errors.updateFailed")));
          },
        },
      );
      return;
    }

    if (data.type === "service") {
      const media = (data.images || []).map((image, index) => ({
        fileUrl: image,
        mediaType: "image" as const,
        description: "",
        order: index,
      }));

      createServiceMutation.mutate(
        {
          data: {
            name: data.name.trim(),
            description: data.description.trim(),
            category: data.category.trim(),
            price: data.price.trim(),
            currency: data.currency,
            deliveryTime: data.deliveryTime.trim(),
            region: data.region.trim(),
            media,
          },
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["/api/v1/services/myServices/"] });
            setIsModalOpen(false);
            setEditError(null);
          },
          onError: (err) => {
            setEditError(getApiErrorMessage(err, t("productService.errors.createFailed")));
          },
        },
      );
      return;
    }

    setPendingFormData(data);
    setHsModalError(null);
    setIsHsModalOpen(true);
  };

  const handleHsModalClose = () => {
    const isCreating = createProductMutation.isPending || createServiceMutation.isPending;
    if (isCreating) return;
    setIsHsModalOpen(false);
    setPendingFormData(null);
    setHsModalError(null);
  };

  const handleHsModalBack = () => {
    const isCreating = createProductMutation.isPending || createServiceMutation.isPending;
    if (isCreating) return;
    setIsHsModalOpen(false);
    setIsModalOpen(true);
    setHsModalError(null);
  };

  const handleHsCodeConfirm = async (selected: HSCodeSuggestion) => {
    if (!pendingFormData) {
      setHsModalError(t("productService.errors.detailsMissing"));
      return;
    }

    const media = (pendingFormData.images || []).map((image, index) => ({
      fileUrl: image,
      mediaType: "image" as const,
      description: "",
      order: index,
    }));

    setHsModalError(null);

    createProductMutation.mutate(
      {
        data: {
          name: pendingFormData.name.trim(),
          description: pendingFormData.description.trim(),
          category: pendingFormData.category.trim(),
          price: pendingFormData.price.trim(),
          currency: pendingFormData.currency,
          deliveryTime: pendingFormData.deliveryTime.trim(),
          region: pendingFormData.region.trim(),
          hsCode: selected.code,
          media,
          quantity: Number(pendingFormData.quantity || 0),
          quantityUnit: pendingFormData.quantityUnit || "",
        },
      },
      {
        onSuccess: (saved) => {
          setSaveWarnings(saved.warnings ?? []);
          queryClient.invalidateQueries({ queryKey: ["/api/v1/products/myProducts/"] });
          setIsHsModalOpen(false);
          setPendingFormData(null);
        },
        onError: (err) => {
          setHsModalError(getApiErrorMessage(err, t("productService.errors.createFailed")));
        },
      },
    );
  };

  return (
    <DashboardLayout>
      <header className="product-service__header">
        <div>
          <h1 className="dashboard__title">{t("productService.title")}</h1>
          <p className="dashboard__subtitle">{t("productService.subtitle")}</p>
        </div>
        <button type="button" className="product-service__add-button" onClick={handleOpenModal}>
          <i className="fa-solid fa-plus" />
          <span>{t("productService.add")}</span>
        </button>
      </header>

      {loading ? (
        <div className="product-service__overview-cards">
          <DashboardCardSkeleton count={3} />
        </div>
      ) : (
        <div className="product-service__overview-cards">
          <article className="product-service__overview-card">
            <div className="product-service__overview-icon product-service__overview-icon--purple">
              <i className="fa-solid fa-cube" />
            </div>
            <div className="product-service__overview-content">
              <p className="product-service__overview-label">{t("productService.overview.total")}</p>
              <p className="product-service__overview-value">{products.length}</p>
            </div>
          </article>

          <article className="product-service__overview-card">
            <div className="product-service__overview-icon product-service__overview-icon--purple">
              <i className="fa-solid fa-cart-shopping" />
            </div>
            <div className="product-service__overview-content">
              <p className="product-service__overview-label">{t("productService.overview.products")}</p>
              <p className="product-service__overview-value">{products.filter((p) => p.type === "product").length}</p>
            </div>
          </article>

          <article className="product-service__overview-card">
            <div className="product-service__overview-icon product-service__overview-icon--orange">
              <i className="fa-solid fa-file-lines" />
            </div>
            <div className="product-service__overview-content">
              <p className="product-service__overview-label">{t("productService.overview.services")}</p>
              <p className="product-service__overview-value">{products.filter((p) => p.type === "service").length}</p>
            </div>
          </article>
        </div>
      )}

      <section className="product-service__table-section">
        {!!listError && (
          <div className="product-service__list-error" role="alert">
            {t("productService.errors.loadFailed")}
            <button
              type="button"
              onClick={() => {
                queryClient.invalidateQueries({
                  queryKey: ["/api/v1/products/myProducts/"],
                });
                queryClient.invalidateQueries({
                  queryKey: ["/api/v1/services/myServices/"],
                });
              }}
              className="product-service__retry-button"
            >
              {t("common:actions.retry")}
            </button>
          </div>
        )}
        {saveWarnings.length > 0 && (
          <div className="product-service__save-warning" role="status">
            <i className="fa-solid fa-triangle-exclamation" aria-hidden />
            <div>
              {saveWarnings.map((w) => (
                <p key={`${w.field}-${w.message}`}>{w.message}</p>
              ))}
            </div>
            <button
              type="button"
              className="product-service__retry-button"
              onClick={() => setSaveWarnings([])}
              aria-label={t("productService.dismissWarning")}
            >
              {t("productService.dismiss")}
            </button>
          </div>
        )}
        <div className="product-service__search-wrapper">
          <div className="product-service__table-search">
            <i className="fa-regular fa-magnifying-glass" />
            <input
              type="search"
              placeholder={t("productService.searchPlaceholder")}
              aria-label={t("productService.searchLabel")}
              value={tableSearch}
              onChange={(e) => setTableSearch(e.target.value)}
            />
          </div>
        </div>

        <div className="product-service__table">
          <div className="product-service__table-row product-service__table-row--head">
            <span>{t("common:labels.name")}</span>
            <span>{t("productService.table.type")}</span>
            <span>{t("productService.table.hsCode")}</span>
            <span>{t("productService.table.category")}</span>
            <span>{t("common:labels.actions")}</span>
          </div>

          {normalizedTableSearch && visibleProducts.length === 0 && (
            <p className="product-service__table-empty">{t("productService.noMatches")}</p>
          )}
          {visibleProducts.map((item) => (
            <div key={item.id} className="product-service__table-row product-service__table-row--data">
              <span className="product-service__table-cell-name">
                <i className="fa-solid fa-cube" />
                <div>
                  <strong>{item.name}</strong>
                  <span className="product-service__table-description">{item.description}</span>
                </div>
              </span>
              <span>
                <span className={`product-service__type-badge product-service__type-badge--${item.type}`}>
                  {t(`productService.typeLabel.${item.type}`)}
                </span>
              </span>
              <span className="product-service__table-cell-hs">
                <i className="fa-solid fa-tag" />
                <div>
                  <strong>{item.hsCode || "—"}</strong>
                  {item.hsCodeUnverified && (
                    <span className="product-service__hs-unverified" title={t("productService.unverifiedTitle")}>
                      {t("productService.unverified")}
                    </span>
                  )}
                  <span className="product-service__table-description">{item.hsDescription}</span>
                </div>
              </span>
              <span>{item.category}</span>
              <span className="product-service__table-actions">
                {item.hsCode ? (
                  <Link
                    to={`/b2b/duty-estimate?hs=${encodeURIComponent(item.hsCode)}`}
                    className="product-service__action-button"
                    aria-label={t("productService.estimateDuty")}
                    title={t("productService.estimateDuty")}
                  >
                    <i className="fa-solid fa-calculator" />
                  </Link>
                ) : null}
                <button
                  type="button"
                  className="product-service__action-button"
                  aria-label={t("common:actions.edit")}
                  onClick={() => handleEdit(item)}
                >
                  <i className="fa-regular fa-pen-to-square" />
                </button>
                <button
                  type="button"
                  className="product-service__action-button"
                  aria-label={t("common:actions.delete")}
                  onClick={() => handleDeleteClick(item)}
                >
                  <i className="fa-regular fa-trash-can" />
                </button>
              </span>
            </div>
          ))}
        </div>
      </section>

      <AddProductServiceModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        isSubmitting={
          updateProductMutation.isPending || updateServiceMutation.isPending || createServiceMutation.isPending
        }
        submitError={editError}
        editData={
          editingItem
            ? {
                id: editingItem.id,
                name: editingItem.name,
                description: editingItem.description,
                type: editingItem.type,
                category: editingItem.category,
                quantity: editingItem.quantity || "",
                quantityUnit: editingItem.quantityUnit || "",
                price: editingItem.price || "",
                currency: editingItem.currency || "",
                deliveryTime: editingItem.deliveryTime || "",
                region: editingItem.region || "",
                images: editingItem.images || [],
              }
            : pendingFormData
              ? { ...pendingFormData }
              : null
        }
        onSave={handleSave}
      />

      <HSCodeSelectionModal
        isOpen={isHsModalOpen}
        onClose={handleHsModalClose}
        onBack={handleHsModalBack}
        onConfirm={handleHsCodeConfirm}
        productName={pendingFormData?.name || t("confirmDelete.defaultItemName")}
        productDescription={pendingFormData?.description ?? ""}
        actionLabel={
          pendingFormData?.type === "service" ? t("productService.addService") : t("productService.addProduct")
        }
        isSubmitting={createProductMutation.isPending || createServiceMutation.isPending}
        errorMessage={hsModalError}
      />

      <ConfirmDeleteModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setDeletingItem(null);
        }}
        onConfirm={handleDeleteConfirm}
        itemName={deletingItem?.name || t("confirmDelete.defaultItemName")}
      />
    </DashboardLayout>
  );
};

export default ProductServicePage;
